import fetchProductsV2 from '@dehaat/kisan-app-bl/api/fetchProductsV2'
import { CanceledError } from 'axios'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  IS_PREBOOK_FLOW,
  NAVIGATION_FROM_APP_CODE,
  PRODUCTS_QUERY_LIMIT,
  VENDOR_ID_COOKIE_NAME,
} from '@/constants/common'
import { Product, ProductsQueryType } from '@/models/Product'
import fetchSearchProduct, {
  RelevantSearchItem,
} from '@/utils/api/fetchSearchProduct'
import { formatAllProducts } from '@/utils/cart'
import {
  fixNextPageUrl,
  getCookieValue,
  getLocalStorageKey,
  isFromVrApp,
} from '@/utils/helper'

import useInfiniteScroll from './useInfiniteScroll'

const useSearchProductsV2 = (
  limit = PRODUCTS_QUERY_LIMIT,
  newSearch = false,
) => {
  const { query } = useRouter()
  const { s: encodedSearchQuery }: ProductsQueryType = query
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)
  const [searchedProducts, setSearchedProducts] = useState<Product[]>([])
  const [releventSearchs, setReleventSearchs] = useState<{
    brand: RelevantSearchItem | undefined
    category: RelevantSearchItem | undefined
    suggestionList: string[] | []
  } | null>(null)
  const requestIdRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const [searchingProducts, setSearchingProducts] = useState<boolean>(false)

  const isprebook = !!getLocalStorageKey(IS_PREBOOK_FLOW)

  const onSearchParamsChanged = useCallback(
    async (encodedSearchQuery: string) => {
      const currentRequestId = ++requestIdRef.current
      setSearchingProducts(true)
      const vendorId = getCookieValue(VENDOR_ID_COOKIE_NAME)
      const isVR = isFromVrApp(getCookieValue(NAVIGATION_FROM_APP_CODE))
      setReleventSearchs(null)

      try {
        let res

        if (!isVR && !newSearch) {
          res = await fetchProductsV2(
            null,
            {
              page: 1,
              page_size: limit,
              vendor_id: vendorId,
              query: encodedSearchQuery,
              isprebook,
            },
            abortRef,
          )
        } else {
          res = await fetchSearchProduct(
            '',
            {
              page: 1,
              page_size: limit,
              vendor_id: vendorId,
              query: encodedSearchQuery,
              isprebook,
            },
            abortRef,
          )

          setReleventSearchs({
            brand: res?.brand,
            category: res?.category,
            suggestionList: res?.suggestion_list || [],
          })
        }
        if (currentRequestId !== requestIdRef.current) return

        if (res?.results) {
          const fm = formatAllProducts(res.results)
          setSearchedProducts((prev) => prev.concat(...fm))
          setNextPageUrl(fixNextPageUrl(res.next) || null)
        }
        setSearchingProducts(false)
      } catch (e) {
        console.log('error in fetching search products: ', e)
        if (!(e instanceof CanceledError)) {
          console.log('if error in fetching search products: ', e)
          setSearchingProducts(false)
        }
      }
    },
    [isprebook, limit, newSearch],
  )

  const { infiniteScrollRef } = useInfiniteScroll(
    useCallback(async () => {
      if (nextPageUrl) {
        setSearchingProducts(true)
        try {
          const response = await fetchProductsV2(nextPageUrl)
          if (response?.results) {
            const formatedProducts = formatAllProducts(response.results)
            setSearchedProducts((prevProducts) =>
              prevProducts.concat(...formatedProducts),
            )
            setNextPageUrl(fixNextPageUrl(response.next) || null)
            setSearchingProducts(false)
          }
        } catch (e) {
          if (!(e instanceof CanceledError)) {
            setSearchingProducts(false)
          }
        }
      }
    }, [nextPageUrl]),
  )

  useEffect(() => {
    setSearchedProducts([])
    setNextPageUrl('')
    if (encodedSearchQuery && encodedSearchQuery.length >= 3) {
      if (abortRef.current) {
        abortRef.current.abort()
      }
      if ('AbortController' in window) {
        abortRef.current = new AbortController()
      }
      onSearchParamsChanged(encodedSearchQuery as string)
    }
  }, [encodedSearchQuery, onSearchParamsChanged])

  return {
    infiniteScrollRef,
    searchedProducts,
    searchingProducts,
    searchQuery: encodedSearchQuery
      ? decodeURIComponent(encodedSearchQuery)
      : '',
    releventSearchs,
  }
}

export default useSearchProductsV2
